import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { Container } from 'reactstrap';
import { _axios } from '../../utils/axios';
import { ServerPath } from '../../Constant';

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const HLS_BASE = 'https://live.myparikrama.ai/hls';
const REFRESH_INTERVAL = 15000;

const STATUS_CONFIG = {
    live_now: { label: 'Live Now', bg: '#4CAF50', dot: true },
    recently_live: { label: 'Recently Live', bg: '#2196F3', dot: false },
    processing: { label: 'Processing', bg: '#FF9800', dot: false },
};

const TYPE_CONFIG = {
    cctv: { label: 'RTMP', bg: '#00BCD4' },
    cctv_external: { label: 'EXT', bg: '#FF9800' },
    youtube: { label: 'YT', bg: '#FF0000' },
};

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const formatUptime = (liveNowAt) => {
    if (!liveNowAt) return '—';
    const diff = Math.floor((Date.now() - new Date(liveNowAt).getTime()) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    return `${h}h ${m}m`;
};

const getStreamMode = (stream) => {
    if (stream.stream_type !== 'cctv') return null;
    return stream.rtsp_url ? 'RTSP' : 'RTMP';
};

const getHlsUrl = (stream) => {
    if (stream.stream_status === 'recently_live') {
        return stream.converted_r2_url || null;
    }
    if (stream.stream_type === 'cctv' && stream.stream_key) {
        return `${HLS_BASE}/${stream.stream_key}/index.m3u8`;
    }
    if (stream.stream_type === 'cctv_external') {
        return stream.hls_url || null;
    }
    return null;
};

// ─────────────────────────────────────────────────────────────
// MINI PLAYER — lazy HLS.js loader
// ─────────────────────────────────────────────────────────────
const MiniPlayer = ({ stream, isFullscreen, onClose, onBufferUpdate }) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const [playerState, setPlayerState] = useState('idle'); // idle | loading | live | error | stalled
    const [bufferHealth, setBufferHealth] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const statsTimerRef = useRef(null);

    const hlsUrl = getHlsUrl(stream);

    const destroyHls = useCallback(() => {
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }
        if (statsTimerRef.current) {
            clearInterval(statsTimerRef.current);
            statsTimerRef.current = null;
        }
    }, []);

    const initPlayer = useCallback(() => {
        if (!videoRef.current || !hlsUrl) return;
        setPlayerState('loading');
        setErrorMsg('');

        const startStats = () => {
            statsTimerRef.current = setInterval(() => {
                const v = videoRef.current;
                if (!v) return;
                const buf = v.buffered;
                if (buf.length > 0) {
                    const health = buf.end(buf.length - 1) - v.currentTime;
                    const val = health.toFixed(1);
                    setBufferHealth(val);
                    onBufferUpdate?.(val);
                }
            }, 2000);
        };

        // Native HLS (Safari)
        if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
            videoRef.current.src = hlsUrl;
            videoRef.current.onplaying = () => { setPlayerState('live'); startStats(); };
            videoRef.current.onerror = () => { setPlayerState('error'); setErrorMsg('Playback failed'); };
            videoRef.current.play().catch(() => { });
            return;
        }

        // HLS.js
        if (!window.Hls) {
            setErrorMsg('Loading HLS.js...');
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.7/dist/hls.min.js';
            script.onload = () => initPlayer();
            script.onerror = () => { setPlayerState('error'); setErrorMsg('HLS.js load failed'); };
            document.head.appendChild(script);
            return;
        }

        if (!window.Hls.isSupported()) {
            setPlayerState('error');
            setErrorMsg('HLS not supported');
            return;
        }

        const hls = new window.Hls({
            debug: false,
            enableWorker: true,
            lowLatencyMode: false,
            maxBufferLength: 20,
            maxMaxBufferLength: 40,
            liveSyncDurationCount: 3,
            liveMaxLatencyDurationCount: 8,
            manifestLoadingTimeOut: 10000,
            manifestLoadingMaxRetry: 2,
            fragLoadingTimeOut: 15000,
        });

        hls.loadSource(hlsUrl);
        hls.attachMedia(videoRef.current);

        hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
            videoRef.current?.play().catch(() => { });
        });

        hls.on(window.Hls.Events.ERROR, (_, data) => {
            if (data.fatal) {
                if (data.type === window.Hls.ErrorTypes.NETWORK_ERROR) {
                    hls.startLoad();
                } else if (data.type === window.Hls.ErrorTypes.MEDIA_ERROR) {
                    hls.recoverMediaError();
                } else {
                    setPlayerState('error');
                    setErrorMsg('Stream unavailable');
                    destroyHls();
                }
            }
        });

        videoRef.current.onplaying = () => { setPlayerState('live'); startStats(); };
        videoRef.current.onwaiting = () => setPlayerState('loading');
        videoRef.current.onstalled = () => setPlayerState('stalled');

        hlsRef.current = hls;
    }, [hlsUrl, destroyHls]);

    useEffect(() => {
        return () => destroyHls();
    }, [destroyHls]);

    if (!hlsUrl) {
        return (
            <div style={styles.playerEmpty}>
                <span style={{ fontSize: '28px', opacity: 0.4 }}>📡</span>
                <span style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                    {stream.stream_status === 'recently_live' ? 'No recording URL' : 'No HLS URL'}
                </span>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}>
            <video
                ref={videoRef}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: playerState === 'idle' ? 'none' : 'block' }}
                autoPlay
                muted
                playsInline
                controls={isFullscreen}
            />

            {/* Idle overlay */}
            {playerState === 'idle' && (
                <div style={styles.playerOverlay}>
                    <button style={styles.playBtn} onClick={initPlayer}>
                        Preview
                    </button>
                </div>
            )}

            {/* Loading overlay */}
            {(playerState === 'loading' || playerState === 'stalled') && (
                <div style={styles.playerOverlay}>
                    <div style={styles.spinner} />
                    <span style={{ fontSize: '11px', color: '#ccc', marginTop: '8px' }}>
                        {playerState === 'stalled' ? 'Stalled...' : 'Connecting...'}
                    </span>
                </div>
            )}

            {/* Error overlay */}
            {playerState === 'error' && (
                <div style={styles.playerOverlay}>
                    <span style={{ fontSize: '22px' }}>⚠️</span>
                    <span style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '6px' }}>{errorMsg}</span>
                    <button style={{ ...styles.playBtn, marginTop: '10px', fontSize: '11px', padding: '5px 12px' }} onClick={() => { setPlayerState('idle'); destroyHls(); }}>
                        Retry
                    </button>
                </div>
            )}

            {/* Buffer health badge (bottom right, only when live) */}
            {/* {playerState === 'live' && bufferHealth !== null && !isFullscreen && (
                <div style={{
                    position: 'absolute', bottom: '6px', right: '6px',
                    background: parseFloat(bufferHealth) > 2 ? 'rgba(34,197,94,0.85)' : parseFloat(bufferHealth) > 0.5 ? 'rgba(245,158,11,0.85)' : 'rgba(239,68,68,0.85)',
                    color: '#fff', fontSize: '10px', fontWeight: '600',
                    padding: '2px 7px', borderRadius: '10px',
                }}>
                    {bufferHealth}s buf
                </div>
            )} */}

            {/* Stop button when playing */}
            {playerState === 'live' && !isFullscreen && (
                <button
                    style={styles.stopBtn}
                    onClick={() => { destroyHls(); setPlayerState('idle'); setBufferHealth(null); if (videoRef.current) { videoRef.current.src = ''; } }}
                    title="Stop Preview"
                >
                    Stop
                </button>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// STREAM CARD
// ─────────────────────────────────────────────────────────────
const StreamCard = ({ stream }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [uptimeStr, setUptimeStr] = useState(() => formatUptime(stream.live_now_at));
    const [liveBuffer, setLiveBuffer] = useState(null);

    useEffect(() => {
        const timer = setInterval(() => setUptimeStr(formatUptime(stream.live_now_at)), 5000);
        return () => clearInterval(timer);
    }, [stream.live_now_at]);

    const statusCfg = STATUS_CONFIG[stream.stream_status] || { label: stream.stream_status, bg: '#9E9E9E', dot: false };
    const typeCfg = TYPE_CONFIG[stream.stream_type] || { label: stream.stream_type, bg: '#9E9E9E' };
    const mode = getStreamMode(stream);

    return (
        <>
            <div style={styles.card}>
                {/* Video area */}
                <div style={styles.cardVideo}>
                    <MiniPlayer stream={stream} isFullscreen={false} onBufferUpdate={setLiveBuffer} />

                    {/* Top badges */}
                    <div style={styles.cardTopRow}>
                        <span style={{ ...styles.badge, background: statusCfg.bg, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {statusCfg.dot && <span style={styles.liveDot} />}
                            {statusCfg.label}
                        </span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            {mode
                                ? <span style={{ ...styles.badge, background: mode === 'RTSP' ? '#7B1FA2' : '#00BCD4' }}>{mode}</span>
                                : <span style={{ ...styles.badge, background: typeCfg.bg }}>{typeCfg.label}</span>
                            }
                        </div>
                    </div>

                    {/* Fullscreen button */}
                    <button style={styles.fullscreenBtn} onClick={() => setIsFullscreen(true)} title="Fullscreen">⛶</button>
                </div>

                {/* Info area */}
                <div style={styles.cardInfo}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
                        <div style={styles.cardTitle} title={stream.stream_title}>{stream.stream_title}</div>
                        {liveBuffer !== null && (
                            <span style={{
                                fontSize: '10px', fontWeight: '600', flexShrink: 0, marginLeft: '8px',
                                color: parseFloat(liveBuffer) > 2 ? '#2E7D32' : parseFloat(liveBuffer) > 0.5 ? '#E65100' : '#C62828',
                                background: parseFloat(liveBuffer) > 2 ? '#E8F5E9' : parseFloat(liveBuffer) > 0.5 ? '#FFF3E0' : '#FFEBEE',
                                borderRadius: '10px', padding: '2px 8px', whiteSpace: 'nowrap'
                            }}>
                                {liveBuffer}s buf
                            </span>
                        )}
                    </div>
                    <div style={styles.cardKey} title={stream.stream_key}>{stream.stream_key || '—'}</div>


                    <div style={styles.metaRow}>
                        <div style={styles.metaItem}>
                            <span style={styles.metaLabel}>Uptime</span>
                            <span style={styles.metaValue}>{uptimeStr}</span>
                        </div>
                        <div style={styles.metaItem}>
                            <span style={styles.metaLabel}>Category</span>
                            <span style={{ ...styles.metaValue, fontSize: '10px' }}>
                                {stream.stream_category?.replace('live_', '').replace('_', ' ') || '—'}
                            </span>
                        </div>
                        <div style={styles.metaItem}>
                            <span style={styles.metaLabel}>Auto Rec</span>
                            <span style={{ ...styles.metaValue, color: stream.auto_convert ? '#4CAF50' : '#999' }}>
                                {stream.auto_convert ? 'ON' : 'OFF'}
                            </span>
                        </div>
                        <div style={styles.metaItem}>
                            <span style={styles.metaLabel}>Health</span>
                            <span style={{
                                ...styles.metaValue,
                                color: stream.health_status === 'ok' ? '#4CAF50' : stream.health_status === 'error' ? '#f44336' : stream.health_status === 'warning' ? '#FF9800' : stream.health_status === 'offline' ? '#D32F2F' : '#999'
                            }}>
                                {stream.health_status?.toUpperCase() || 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fullscreen modal */}
            {isFullscreen && (
                <div style={styles.fsOverlay} onClick={() => setIsFullscreen(false)}>
                    <div style={styles.fsModal} onClick={e => e.stopPropagation()}>
                        <div style={styles.fsHeader}>
                            <div>
                                <span style={{ fontWeight: '600', fontSize: '15px' }}>{stream.stream_title}</span>
                                <span style={{ marginLeft: '10px', fontSize: '12px', color: '#999' }}>{stream.stream_key}</span>
                            </div>
                            <button style={styles.fsClose} onClick={() => setIsFullscreen(false)}>✕</button>
                        </div>
                        <div style={styles.fsVideo}>
                            <MiniPlayer stream={stream} isFullscreen={false} onBufferUpdate={setLiveBuffer} />
                        </div>
                        <div style={styles.fsInfo}>
                            <span style={{ ...styles.badge, background: statusCfg.bg }}>{statusCfg.label}</span>
                            <span style={{ ...styles.badge, background: mode === 'RTSP' ? '#7B1FA2' : '#00BCD4' }}>
                                {mode || typeCfg.label}
                            </span>
                            <span style={{ marginLeft: '12px', fontSize: '13px', color: '#666' }}>Uptime: <strong>{uptimeStr}</strong></span>
                            <span style={{ marginLeft: '12px', fontSize: '13px', color: '#666' }}>Health: <strong style={{ color: stream.health_status === 'ok' ? '#4CAF50' : '#f44336' }}>{stream.health_status?.toUpperCase() || 'N/A'}</strong></span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const StreamMonitor = () => {
    const [streams, setStreams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [filter, setFilter] = useState('all'); // all | live_now | recently_live
    const [ytStreams, setYtStreams] = useState([]);
    const [youtubeDbStreams, setYoutubeDbStreams] = useState([]);
    const timerRef = useRef(null);

    const fetchYoutubeStatus = useCallback(async () => {
        try {
            const res = await _axios.get(`${ServerPath}/stream-status`);
            setYtStreams(res.data || []);
        } catch (err) {
            console.error("YT status error:", err);
        }
    }, []);

    const fetchStreams = useCallback(async () => {
        try {
            const response = await _axios.get(`${ServerPath}/live-darshan/all`);
            if (response.data.success === 1) {
                const active = response.data.data
                    .filter(s => (s.streamStatus === 'live_now' || s.streamStatus === 'recently_live' || s.streamStatus === 'processing') && s.streamType === 'cctv')
                    .map(s => ({
                        id: s.id,
                        stream_type: s.streamType,
                        stream_status: s.streamStatus,
                        stream_key: s.cctv?.streamKey || null,
                        stream_title: s.streamTitle,
                        stream_category: s.streamCategory,
                        hls_url: s.cctv?.hlsUrl || null,
                        rtsp_url: s.cctv?.rtspUrl || null,
                        converted_r2_url: s.convertedR2Url || null,
                        auto_convert: s.autoConvert || false,
                        health_status: s.healthStatus || null,
                        live_now_at: s.liveNowAt || null,
                        recently_live_at: s.recentlyLiveAt || null,
                        orientation: s.orientation || 'portrait',
                        thumbnail_url: s.thumbnailUrl || null,
                    }));
                setStreams(active);

                const ytDb = response.data.data
                    .filter(s => s.streamType === 'youtube')
                    .map(s => ({
                        id: s.id,
                        stream_title: s.streamTitle,
                        stream_status: s.streamStatus || 'draft',
                        youtube_video_id: s.youtubeVideoId || null,
                    }));
                setYoutubeDbStreams(ytDb);
                setLastRefresh(new Date());
            }
        } catch (err) {
            console.error('StreamMonitor fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStreams();
        fetchYoutubeStatus();
    }, [fetchStreams, fetchYoutubeStatus]);

    useEffect(() => {
        if (autoRefresh) {
            timerRef.current = setInterval(() => {
                fetchStreams();
                fetchYoutubeStatus();
            }, REFRESH_INTERVAL);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [autoRefresh, fetchStreams, fetchYoutubeStatus]);

    const filtered = streams.filter(s => filter === 'all' ? true : s.stream_status === filter);

    const liveCount = streams.filter(s => s.stream_status === 'live_now').length;
    const recentCount = streams.filter(s => s.stream_status === 'recently_live').length;

    return (
        <Fragment>
            <style>{monitorStyles}</style>
            <Container fluid>
            {/* 🔴 YOUTUBE LIVE STATUS TABLE */}
{youtubeDbStreams.length > 0 && (
    <div style={{ background: "#fff", borderRadius: "10px", border: "1px solid #eee", padding: "16px", marginBottom: "16px" }}>
        <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "15px" }}>YouTube Streams</div>
        {[
            { key: 'live_now',      label: 'Live Now',      color: '#4CAF50' },
            { key: 'recently_live', label: 'Recently Live', color: '#2196F3' },
            { key: 'archived',      label: 'Archived',      color: '#424242' },
            { key: 'draft',         label: 'Draft',         color: '#9E9E9E' },
        ].map(group => {
            const groupItems = youtubeDbStreams.filter(s => s.stream_status === group.key);
            if (groupItems.length === 0) return null;
            return (
                <div key={group.key} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: group.color, display: 'inline-block', flexShrink: 0 }} />
                        <span style={{ fontWeight: '600', fontSize: '13px', color: '#4A5568' }}>{group.label}</span>
                        <span style={{ fontSize: '11px', color: '#fff', background: group.color, borderRadius: '10px', padding: '1px 7px', fontWeight: '600' }}>
                            {groupItems.length}
                        </span>
                        <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px' }}>
                        {groupItems.map(s => {
                            const ytApiStatus = ytStreams.find(y => y.videoId === s.youtube_video_id);
                            return (
                                <div key={s.id} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.stream_title}</span>
                                    {ytApiStatus && (
                                        <span style={{
                                            fontWeight: '600', flexShrink: 0, fontSize: '11px',
                                            color: ytApiStatus.status === 'LIVE' ? '#4CAF50' : '#f44336'
                                        }}>
                                            {ytApiStatus.status}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        })}
    </div>
)}
                {/* Header */}
                <div style={styles.header}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Stream Monitor</h3>
                        <span style={{ fontSize: '12px', color: '#999' }}>
                            {lastRefresh ? `Last updated: ${lastRefresh.toLocaleTimeString()}` : 'Loading...'}
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {/* Summary pills */}
                        <span style={{ ...styles.badge, background: '#4CAF50', fontSize: '12px', padding: '4px 12px' }}>
                            {liveCount} Live
                        </span>
                        <span style={{ ...styles.badge, background: '#2196F3', fontSize: '12px', padding: '4px 12px' }}>
                            {recentCount} Recorded
                        </span>

                        {/* Filter */}
                        <div style={{ display: 'flex', gap: '4px' }}>
                            {[
                                { value: 'all', label: 'All' },
                                { value: 'live_now', label: 'Live Now' },
                                { value: 'recently_live', label: 'Recently Live' },
                            ].map(f => (
                                <button
                                    key={f.value}
                                    style={{ ...styles.filterBtn, ...(filter === f.value ? styles.filterBtnActive : {}) }}
                                    onClick={() => setFilter(f.value)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>

                        {/* Auto refresh toggle */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}>
                            <div
                                style={{
                                    width: '36px', height: '20px', borderRadius: '10px',
                                    background: autoRefresh ? '#4CAF50' : '#ccc',
                                    position: 'relative', transition: 'background 0.2s', cursor: 'pointer'
                                }}
                                onClick={() => setAutoRefresh(p => !p)}
                            >
                                <div style={{
                                    position: 'absolute', top: '2px',
                                    left: autoRefresh ? '18px' : '2px',
                                    width: '16px', height: '16px', borderRadius: '50%',
                                    background: '#fff', transition: 'left 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                }} />
                            </div>
                            Auto-refresh
                        </label>

                        {/* Manual refresh */}
                        <button style={styles.refreshBtn} onClick={fetchStreams}>↻ Refresh</button>
                    </div>
                </div>

                {/* Grid */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
                        <div style={{ ...styles.spinner, margin: '0 auto 12px' }} />
                        Loading streams...
                    </div>
                ) : filtered.length === 0 ? (
                    <div style={styles.emptyState}>
                        <span style={{ fontSize: '40px', opacity: 0.3 }}>📡</span>
                        <p style={{ color: '#999', marginTop: '12px' }}>
                            {filter === 'all' ? 'No active streams right now.' : `No ${filter.replace('_', ' ')} streams.`}
                        </p>
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {filtered.map(stream => (
                            <StreamCard key={stream.id} stream={stream} />
                        ))}
                    </div>
                )}
            </Container>
        </Fragment>
    );
};

// ─────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────
const styles = {
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '16px',
    },
    card: {
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #E8E8E8',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        isolation: 'isolate',  // ADD THIS
    },
    cardVideo: {
        position: 'relative',
        aspectRatio: '16/9',
        background: '#111',
        overflow: 'hidden',
        isolation: 'isolate',  // ADD THIS
    },
    cardTopRow: {
        position: 'absolute',
        top: '8px',
        left: '8px',
        right: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        zIndex: 10,
        pointerEvents: 'none',
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '20px',
        padding: '3px 8px',
        fontSize: '10px',
        fontWeight: '700',
        color: '#fff',
        letterSpacing: '0.02em',
    },
    liveDot: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: '#fff',
        animation: 'livePulse 1.4s ease infinite',
    },
    fullscreenBtn: {
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        background: 'rgba(0,0,0,0.5)',
        border: 'none',
        color: '#fff',
        borderRadius: '6px',
        padding: '3px 7px',
        fontSize: '14px',
        cursor: 'pointer',
        zIndex: 10,
    },
    stopBtn: {
        position: 'absolute',
        bottom: '8px',
        left: '8px',
        background: 'rgba(220,50,50,0.7)',
        border: 'none',
        color: '#fff',
        borderRadius: '6px',
        padding: '3px 7px',
        fontSize: '12px',
        cursor: 'pointer',
        zIndex: 10,
    },
    cardInfo: {
        padding: '12px 14px',
    },
    cardTitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#1a1a1a',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginBottom: '2px',
    },
    cardKey: {
        fontSize: '11px',
        fontFamily: 'monospace',
        color: '#888',
        marginBottom: '10px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    metaRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
        gap: '6px',
    },
    metaItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#F8F8F8',
        borderRadius: '8px',
        padding: '6px 4px',
    },
    metaLabel: {
        fontSize: '9px',
        color: '#aaa',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: '2px',
    },
    metaValue: {
        fontSize: '11px',
        fontWeight: '700',
        color: '#333',
    },
    playerEmpty: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#111',
    },
    playerOverlay: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        zIndex: 5,
    },
    playBtn: {
        background: 'rgba(255,107,53,0.9)',
        border: 'none',
        color: '#fff',
        borderRadius: '20px',
        padding: '8px 20px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    spinner: {
        width: '24px',
        height: '24px',
        border: '3px solid rgba(255,255,255,0.2)',
        borderTopColor: '#fff',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    filterBtn: {
        background: '#F0F0F0',
        border: '1px solid #E0E0E0',
        borderRadius: '20px',
        padding: '5px 14px',
        fontSize: '12px',
        fontWeight: '500',
        cursor: 'pointer',
        color: '#555',
    },
    filterBtnActive: {
        background: '#5662F6',
        borderColor: '#5662F6',
        color: '#fff',
    },
    refreshBtn: {
        background: '#F0F0F0',
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
        padding: '6px 14px',
        fontSize: '13px',
        cursor: 'pointer',
        color: '#555',
    },
    emptyState: {
        textAlign: 'center',
        padding: '80px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    // Fullscreen modal
    fsOverlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
    },
    fsModal: {
        background: '#1a1a1a',
        borderRadius: '14px',
        width: '100%',
        maxWidth: '900px',
        overflow: 'hidden',
    },
    fsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 18px',
        borderBottom: '1px solid #333',
        color: '#fff',
    },
    fsVideo: {
        aspectRatio: '16/9',
        background: '#000',
        position: 'relative',
        overflow: 'hidden',
    },
    fsInfo: {
        padding: '12px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'wrap',
    },
    fsClose: {
        background: 'rgba(255,255,255,0.1)',
        border: 'none',
        color: '#fff',
        borderRadius: '6px',
        padding: '4px 10px',
        cursor: 'pointer',
        fontSize: '14px',
    },
};

const monitorStyles = `
    @keyframes livePulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.4; transform: scale(1.3); }
    }
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;

export default StreamMonitor;